/**
 * "Mapping-Abdeckung" — the donut and its legend.
 *
 * TWO THINGS THE DESIGN REFERENCE GETS WRONG about this data, and why this
 * component does not follow it literally:
 *
 * 1. The reference shows four categories that add up to the total. They do not.
 *    `invalid_mapped_or_unresolved` is documented in the backend schema as a
 *    SUB-COUNT of `mapped_quad` — raw full-quad mappings whose ids do not
 *    resolve org-scoped — and is deliberately NOT subtracted from it, so the
 *    invariant is `total = mapped_quad + unmapped + partial_maybe_invalid`.
 *    Drawing it as a fourth slice would double-count every unresolvable mapping
 *    and make the ring exceed the total. It gets its own "davon" row instead.
 *    In the reference it happens to be 0, which is why the error is invisible
 *    there.
 *
 * 2. Percentages are computed here from the counts, never taken from a fixture.
 *
 * The ring is inline SVG. `recharts` is in the project and used by three other
 * dashboards, but a three-segment ring is one `stroke-dasharray` per segment and
 * bringing a charting bundle into this page to draw it would cost more than it
 * explains. Colours come through `currentColor` on themed wrappers, so the ring
 * follows light/dark like everything else.
 */
import React from "react";

import BexioCoverageExamples from "./BexioCoverageExamples";

const RADIUS = 42;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/** Segment colours as themed text colours; the ring reads them via currentColor. */
const SEGMENTS = [
  {
    key: "mapped",
    label: "Zugeordnet",
    tone: "text-emerald-600 dark:text-emerald-400",
    dot: "bg-emerald-600 dark:bg-emerald-400",
  },
  {
    key: "unmapped",
    label: "Nicht zugeordnet",
    tone: "text-red-600 dark:text-red-400",
    dot: "bg-red-600 dark:bg-red-400",
  },
  {
    key: "partial",
    label: "Teilweise / prüfen",
    tone: "text-amber-500 dark:text-amber-400",
    dot: "bg-amber-500 dark:bg-amber-400",
  },
];

function pct(count, total) {
  // Total is 0 on a fresh org and on any org with nothing mirrored yet.
  if (!total) return 0;
  return (count / total) * 100;
}

function fmtPct(value) {
  return `${value.toLocaleString("de-CH", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

export default function BexioMappingCoverageCard({ coverage, currency }) {
  const total = Number(coverage?.total) || 0;
  const counts = {
    mapped: Number(coverage?.mapped_quad) || 0,
    unmapped: Number(coverage?.unmapped) || 0,
    partial: Number(coverage?.partial_maybe_invalid) || 0,
  };
  const unresolved = Number(coverage?.invalid_mapped_or_unresolved) || 0;

  // Offsets walk the ring so segments sit end to end.
  let walked = 0;
  const arcs = SEGMENTS.map((s) => {
    const share = pct(counts[s.key], total);
    const arc = {
      ...s,
      count: counts[s.key],
      share,
      dash: (share / 100) * CIRCUMFERENCE,
      offset: -(walked / 100) * CIRCUMFERENCE,
    };
    walked += share;
    return arc;
  });

  return (
    <section
      aria-labelledby="bexio-mapping-title"
      className="rounded-xl border border-border bg-card p-5"
    >
      <h2 id="bexio-mapping-title" className="text-sm font-semibold text-foreground">
        Mapping-Abdeckung
      </h2>

      {total === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Noch keine Zuordnungsdaten.
        </p>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-6">
          <svg
            viewBox="0 0 100 100"
            className="h-32 w-32 shrink-0 -rotate-90"
            role="img"
            aria-label="Verteilung der Mapping-Zustände"
          >
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              strokeWidth="14"
              className="stroke-muted"
            />
            {arcs
              .filter((a) => a.count > 0)
              .map((a) => (
                <circle
                  key={a.key}
                  cx="50"
                  cy="50"
                  r={RADIUS}
                  fill="none"
                  strokeWidth="14"
                  stroke="currentColor"
                  className={a.tone}
                  strokeDasharray={`${a.dash} ${CIRCUMFERENCE - a.dash}`}
                  strokeDashoffset={a.offset}
                />
              ))}
          </svg>

          {/* The legend is the accessible reading of the ring, not decoration:
              the SVG carries no per-segment text. */}
          <dl className="min-w-0 flex-1 space-y-2">
            {arcs.map((a) => (
              <div key={a.key} className="flex items-center gap-2 text-sm">
                <span aria-hidden="true" className={`h-2.5 w-2.5 shrink-0 rounded-full ${a.dot}`} />
                <dt className="flex-1 truncate text-muted-foreground">{a.label}</dt>
                <dd className="shrink-0 tabular-nums text-foreground">
                  {a.count}{" "}
                  <span className="text-muted-foreground">({fmtPct(a.share)})</span>
                </dd>
              </div>
            ))}
            <div className="flex items-center gap-2 pl-[18px] text-xs">
              {/* Indented and prefixed with "davon" because this is a subset of
                  "Zugeordnet", not a fourth category (see the file docstring). */}
              <dt className="flex-1 truncate text-muted-foreground">
                davon nicht auflösbar
              </dt>
              <dd className="shrink-0 tabular-nums text-muted-foreground">
                {unresolved}
              </dd>
            </div>
          </dl>
        </div>
      )}

      {/* Which invoices are behind the two problem counts — the donut says how
          many, this says which. Renders nothing when there is nothing to name. */}
      <BexioCoverageExamples coverage={coverage} currency={currency} />

      <div className="mt-4 flex items-center justify-between border-t border-border pt-3 text-sm">
        <span className="text-muted-foreground">Total</span>
        <span className="font-semibold tabular-nums text-foreground">{total}</span>
      </div>
    </section>
  );
}
